import { useCustomer } from "@/contexts/CustomerContext";
import { RootState } from "@/store";
import classNames from "@/utils/helpers/ClassesUtils";
import { Dialog, Tab, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "@/components/pagination";
import CustomerProfile from "./CustomerProfile";
import Spinner from "@/components/loader/Spinner";
import { useAddressAutocomplete } from "@/contexts/useAddressContext";
import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import { Order } from "@/types/order";
import {
  useFetchCustomerPointsQuery,
  useUpdateCustomerProfileMutation,
} from "@/store/api/userApi";
import { useFetchOrdersQuery } from "@/store/api/orderApi";
import OrderDetails from "./OrderDetails";
import Button from "../ui/Button";
import CloseIcon from "@/assets/img/CloseIcon";

const ProfilePage = () => {
  const { login, customer, logout } = useCustomer();
  const branchId = useSelector((state: RootState) => state.cart.branchId);
  const [isOpen, setIsOpen] = useState({
    profile: false,
    details: false,
    retry: false,
  });
  const [order, setOrder] = useState<Order | null>(null);
  const [paymentStatus, setPaymentStatus] = useState("Paid");
  const [page, setPage] = useState(1);
  const limit = 5;
  const navigate = useNavigate();
  const merchantSlug = useMerchantSlug();
  const branch = JSON.parse(localStorage.getItem("selectedBranch") || "");

  const [form, setForm] = useState({
    name: customer?.name ?? "",
    avatar: "",
    email: customer?.email ?? "",
    phone: customer?.phone ?? "",
    wallet_points: parseFloat(customer?.wallet_points ?? "0.00").toFixed(2),
  });

  useEffect(() => {
    if (!customer) {
      navigate(`${merchantSlug}/customer-login`);
    }
  }, [customer, merchantSlug]);

  const { address, setAddress, handlePlaceSelected, handleInputChange } =
    useAddressAutocomplete({
      street: "",
      city: "",
      province: "",
      country: "",
      postal_code: "",
    });

  useEffect(() => {
    if (customer) {
      setAddress({
        street: customer.street ?? "",
        city: customer.city ?? "",
        province: customer.province ?? "",
        country: customer.country ?? "",
        postal_code: customer.postal_code ?? "",
      });
    }
  }, [customer, order, setAddress]);

  const { data: orders, isLoading: ordersLoading } = useFetchOrdersQuery(
    {
      customer: customer?.name ?? "",
      page_size: limit,
      payment_status: paymentStatus,
      page: page,
      branch: branchId ?? "",
      pageSize: limit,
    },
    {
      skip: !customer?.name || !branchId,
    },
  );

  const { data: points, isLoading: loading } = useFetchCustomerPointsQuery(
    {
      phone: customer?.phone ? `%2B${customer.phone.substring(1)}` : "",
      branch: branchId ?? "",
    },
    {
      skip: !customer?.phone || !branchId,
    },
  );

  const [updateProfile] = useUpdateCustomerProfileMutation();

  useEffect(() => {
    if (points?.points) {
      setForm((prev) => ({
        ...prev,
        wallet_points: points.points
          ? parseFloat(points.points).toFixed(2)
          : "0.00",
      }));
    }
  }, [points]);

  const displayPoints = () => {
    try {
      if (!form?.wallet_points) return "0 points";
      const pointValue = String(form.wallet_points).split(".")[0];
      return `${pointValue} points`;
    } catch (error) {
      console.error("Error formatting points:", error);
      return "0 points";
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = {
      ...form,
      street: address.street ?? "",
      city: address.city ?? "",
      state: address.province ?? "",
      province: address.province ?? "",
      country: address.country ?? "",
      pincode: address.postal_code ?? "",
      postal_code: address.postal_code ?? "",
    };
    await updateProfile(data).unwrap();
    login(data);
    setIsOpen((p) => ({ ...p, profile: false }));
  };

  const handlePageChange = (page: number) => setPage(page);
  const handlePaymentStatus = (status: string) => {
    setPaymentStatus(status);
    setPage(1);
  };

  const renderOrders = () => {
    if (ordersLoading) {
      return (
        <div className="flex justify-center items-center h-96 p-2">
          <p className="text-gray-500 text-center mt-4">Loading orders...</p>
        </div>
      );
    }

    const orderResults = orders?.results ?? [];

    if (orderResults.length === 0) {
      return (
        <div className="flex justify-center items-center h-96 w-[324px]">
          <div className="text-gray-500">
            No {paymentStatus.toLowerCase()} orders found for this branch
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4">
        {orderResults.map((order) => (
          <div
            key={order.order_name || Math.random()}
            onClick={() => {
              setOrder(order);
              setIsOpen((p) => ({ ...p, details: true }));
            }}
            className="flex justify-between items-center text-sm md:text-base"
          >
            <div
              className={classNames(
                "grid grid-cols-3 justify-center w-full border-l-8 p-4 gap-2 rounded-lg shadow-md mb-4",
                order.payment_status === "Paid"
                  ? "border-green-500"
                  : order.payment_status === "Unpaid"
                    ? "border-gray-500"
                    : "border-redCrimson",
              )}
            >
              <div className="col-span-2 flex-1">
                <div className="flex justify-between items-start text-primary">
                  <div>
                    <h3 className="font-bold text-lg">{order.order_name}</h3>
                    <p>${order.total_price}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex flex-col">
                    <span>{order.order_type}</span>
                    <span>
                      {new Date(order.created_at).toDateString()}{" "}
                      {new Date(order.created_at)
                        .toTimeString()
                        .substring(0, 5)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-center space-y-4">
                <button
                  onClick={() => {
                    setOrder(order);
                    setIsOpen((p) => ({ ...p, details: true }));
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 rounded-md"
                >
                  Details
                </button>
              </div>
            </div>
          </div>
        ))}
        {orderResults.length > 0 && orders && "count" in orders && (
          <Pagination
            className="w-full mt-8"
            cPage={page}
            data={{ totalCount: orders.count }}
            defaultPerPage={limit}
            onChange={handlePageChange}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <Transition appear show={isOpen.profile} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsOpen((p) => ({ ...p, profile: false }))}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                  <CustomerProfile
                    handleSubmit={handleSubmit}
                    form={form}
                    setForm={setForm}
                    address={address}
                    handlePlaceSelected={handlePlaceSelected}
                    handleInputChange={handleInputChange}
                    cancelBtn
                    onCancel={() =>
                      setIsOpen((p) => ({ ...p, profile: false }))
                    }
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isOpen.details} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsOpen((p) => ({ ...p, details: false }))}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-primary text-left align-middle shadow-xl transition-all">
                  <OrderDetails
                    id={order?.id ?? 0}
                    onCancel={() =>
                      setIsOpen((p) => ({ ...p, details: false }))
                    }
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Spinner />
        </div>
      )}
      <div className="flex flex-col items-center space-y-10 p-5 md:p-10">
        <Link
          to={`${merchantSlug}`}
          className="text-black hover:underline absolute top-5 right-5 hidden md:block"
        >
          <CloseIcon />
        </Link>
        <div className="flex flex-col md:flex-row md:gap-20">
          <div className="w-full md:w-1/2 flex flex-col space-y-5">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold">Customer Profile</h1>
              <Link
                to={`${merchantSlug}`}
                className="text-black hover:underline md:hidden"
              >
                <CloseIcon />
              </Link>
            </div>
            <div className="grid grid-cols-2 items-center gap-5">
              <div>
                <span className="text-lg text-[#777980]">Customer Name</span>
                <h1 className="ml-3 font-bold">{customer?.name}</h1>
              </div>
              {branch?.loyalty_program_enabled && (
                <div>
                  <span className="text-lg text-[#777980]">Points</span>
                  <h1 className="ml-3 font-bold">{displayPoints()}</h1>
                </div>
              )}
              <div>
                <span className="text-lg text-[#777980]">Phone Number</span>
                <h1 className="ml-3 font-bold">{customer?.phone}</h1>
              </div>
              <div className="col-span-2">
                <span className="text-lg text-[#777980]">Email Address</span>
                <h1 className="ml-3 font-bold">
                  {customer?.email ? customer?.email : "NA"}
                </h1>
              </div>
            </div>
            <div>
              <span className="text-lg text-[#777980]">Billing Address</span>
              <h1 className="ml-3 font-bold">
                {customer?.street}, {customer?.city}, {customer?.province},{" "}
                {customer?.country} - {customer?.postal_code}
              </h1>
            </div>
            <div className="flex flex-col hidden md:flex">
              <Button
                variant="primary"
                text="Back to Store"
                to={`${merchantSlug}`}
                className="h-14 mb-4 flex items-center justify-center"
              />

              <Button
                variant="secondary"
                text="Edit Profile"
                onClick={() => setIsOpen((p) => ({ ...p, profile: true }))}
                className="h-14 mb-4 flex items-center justify-center"
              />
              <Button
                variant="secondary"
                text="Logout"
                onClick={logout}
                className="h-14 mb-4 flex items-center justify-center"
              />
            </div>
          </div>
          <div className="w-full md:w-1/2 flex flex-col space-y-5">
            <h2 className="text-lg lg:text-2xl font-bold mb-4">
              Order History
            </h2>
            <div className="flex flex-col justify-center gap-8">
              <Tab.Group>
                <Tab.List className="grid grid-cols-2 items-center space-x-2 rounded-md border border-gray-300 p-1">
                  <Tab
                    className={({ selected }) =>
                      classNames(
                        "px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150 ease-in-out",
                        selected
                          ? "bg-redCrimson text-white shadow-md outline-none"
                          : "bg-white text-primary hover:bg-redCrimson",
                      )
                    }
                    onClick={() => handlePaymentStatus("Paid")}
                  >
                    Paid Orders
                  </Tab>
                  <Tab
                    className={({ selected }) =>
                      classNames(
                        "px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150 ease-in-out",
                        selected
                          ? "bg-redCrimson text-white shadow-md outline-none"
                          : "bg-white text-primary hover:bg-redCrimson",
                      )
                    }
                    onClick={() => handlePaymentStatus("Unpaid")}
                  >
                    Draft Orders
                  </Tab>
                </Tab.List>
                <Tab.Panels className="flex items-center justify-center">
                  <Tab.Panels>
                    <Tab.Panel>{renderOrders()}</Tab.Panel>
                    <Tab.Panel>{renderOrders()}</Tab.Panel>
                  </Tab.Panels>
                </Tab.Panels>
              </Tab.Group>
            </div>
            <div className="flex flex-col md:hidden">
              <Button
                variant="primary"
                text="Back to Store"
                to={`${merchantSlug}`}
                className="h-14 mb-4 flex items-center justify-center"
              />

              <Button
                variant="secondary"
                text="Edit Profile"
                onClick={() => setIsOpen((p) => ({ ...p, profile: true }))}
                className="h-14 mb-4 flex items-center justify-center"
              />
              <Button
                variant="secondary"
                text="Logout"
                onClick={logout}
                className="h-14 mb-4 flex items-center justify-center"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
